import type { PackageInclusionRecord as OfferPackageInclusionRecord } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';

import { buildOfferBuilderPackages, getPackagePersistId } from '../OfferPackages/utils';
import {
  type PackageInclusionRecord,
  getInclusionCategory,
  getInclusionName,
  getInclusionPersistId,
  getPackageInclusionSortOrder,
  isCustomInclusion,
  isInclusionHidden,
} from '../utils';

export type { PackageInclusionRecord };

export interface MasterInclusion {
  id: string;
  assignedPackageIds: Array<string>;
  category: string;
  categoryId?: string | null;
  currency?: string;
  highlighted: boolean;
  inclusion: string;
  isCustom?: boolean;
  isHidden?: boolean;
  luxPlusOnly: boolean;
  persistId?: string;
  requiresBooking?: boolean;
  rrp?: number | null;
  type?: string;
  unit?: string;
}

export type MasterInclusionEdit = Pick<
  MasterInclusion,
  'category' | 'categoryId' | 'currency' | 'inclusion' | 'isHidden' | 'requiresBooking' | 'rrp' | 'type' | 'unit'
>;

export interface PackageOption {
  id: string;
  name: string;
}

export const TABLE_GRID_COLUMNS =
  '40px minmax(150px, 180px) minmax(260px, 1fr) 110px 130px 130px minmax(160px, 200px) 40px';

export function toMasterInclusionEdit(
  inclusion: MasterInclusion,
  overrides: Partial<MasterInclusionEdit> = {},
): MasterInclusionEdit {
  return {
    category: inclusion.category,
    categoryId: inclusion.categoryId,
    currency: inclusion.currency,
    inclusion: inclusion.inclusion,
    isHidden: inclusion.isHidden,
    requiresBooking: inclusion.requiresBooking,
    rrp: inclusion.rrp,
    type: inclusion.type,
    unit: inclusion.unit,
    ...overrides,
  };
}

type OfferPackageInclusionWithDisplayFields = OfferPackageInclusionRecord &
  PackageInclusionRecord & {
    inclusion: OfferPackageInclusionRecord['inclusion'] & NonNullable<PackageInclusionRecord['inclusion']>;
  };

export function getPackageOptions(packages?: Array<App.Package>): Array<PackageOption> {
  const packageIndexByReference = new Map(packages?.map((offerPackage, index) => [offerPackage, index]));

  return buildOfferBuilderPackages(packages ?? []).map((offerPackage) => ({
    id:
      getPackagePersistId(offerPackage.package) ?? `package-${packageIndexByReference.get(offerPackage.package) ?? 0}`,
    name: offerPackage.name,
  }));
}

export function filterPackageOptions(packageOptions: Array<PackageOption>, searchTerm: string) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  if (!normalizedSearchTerm) {
    return packageOptions;
  }

  return packageOptions.filter((option) => option.name.toLowerCase().includes(normalizedSearchTerm));
}

export function selectPackageOptions(currentPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  return Array.from(new Set(currentPackageIds.concat(packageOptions.map((option) => option.id))));
}

export function clearPackageOptions(currentPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  const packageIdsToClear = new Set(packageOptions.map((option) => option.id));

  return currentPackageIds.filter((packageId) => !packageIdsToClear.has(packageId));
}

function getInclusionKey(inclusion: OfferPackageInclusionWithDisplayFields) {
  return getInclusionPersistId(inclusion) ?? getInclusionName(inclusion).trim().toLowerCase();
}

function getMedian(values: Array<number>) {
  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  return sortedValues.length % 2 === 0
    ? (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2
    : sortedValues[middleIndex];
}

export function buildMasterInclusionsFromOffer(
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<MasterInclusion> {
  if (!packageInclusions) {
    return [];
  }

  const inclusionsById = new Map<
    string,
    { inclusion: MasterInclusion; packageSortOrders: Array<number>; packageSortOrderByPackageId: Map<string, number> }
  >();

  Object.entries(packageInclusions).forEach(([packageId, inclusions]) => {
    inclusions.forEach((rawInclusion) => {
      // Package inclusion payloads can include extra display fields; missing values intentionally fall back to off.
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionName = getInclusionName(inclusion);

      if (!inclusionName || isInclusionHidden(inclusion)) {
        return;
      }

      const inclusionKey = getInclusionKey(inclusion);
      const existingInclusion = inclusionsById.get(inclusionKey);
      const sortOrder = getPackageInclusionSortOrder(rawInclusion);

      if (existingInclusion) {
        if (!existingInclusion.inclusion.assignedPackageIds.includes(packageId)) {
          existingInclusion.inclusion.assignedPackageIds.push(packageId);
        }

        existingInclusion.inclusion.highlighted =
          existingInclusion.inclusion.highlighted || inclusion.style === 'highlighted';
        existingInclusion.inclusion.luxPlusOnly = existingInclusion.inclusion.luxPlusOnly || !!inclusion.lux_plus_tier;

        existingInclusion.packageSortOrders.push(sortOrder);
        existingInclusion.packageSortOrderByPackageId.set(packageId, sortOrder);
        return;
      }

      inclusionsById.set(inclusionKey, {
        inclusion: {
          id: inclusionKey,
          assignedPackageIds: [packageId],
          category: getInclusionCategory(inclusion),
          categoryId: inclusion.inclusion.category_id,
          currency: inclusion.inclusion.currency,
          highlighted: inclusion.style === 'highlighted',
          inclusion: inclusionName,
          isCustom: isCustomInclusion(inclusion),
          isHidden: !!inclusion.inclusion.is_hidden,
          luxPlusOnly: !!inclusion.lux_plus_tier,
          persistId: getInclusionPersistId(inclusion),
          requiresBooking: !!inclusion.inclusion.requires_booking,
          rrp: inclusion.inclusion.rrp,
          type: inclusion.inclusion.type,
          unit: inclusion.inclusion.unit,
        },
        packageSortOrders: [sortOrder],
        packageSortOrderByPackageId: new Map([[packageId, sortOrder]]),
      });
    });
  });

  return Array.from(inclusionsById.values())
    .sort((a, b) => {
      const medianSortOrderDifference = getMedian(a.packageSortOrders) - getMedian(b.packageSortOrders);
      const medianOrder = Math.sign(medianSortOrderDifference);
      const pairwiseDisagreement = a.inclusion.assignedPackageIds.reduce(
        (counts, packageId) => {
          const aSortOrder = a.packageSortOrderByPackageId.get(packageId);
          const bSortOrder = b.packageSortOrderByPackageId.get(packageId);

          if (!aSortOrder || !bSortOrder || aSortOrder === bSortOrder) {
            return counts;
          }

          const packageOrder = Math.sign(aSortOrder - bSortOrder);
          counts.order += packageOrder;

          if (medianOrder === 0 || packageOrder === medianOrder) {
            counts.agreements += 1;
          } else {
            counts.disagreements += 1;
          }

          return counts;
        },
        { agreements: 0, disagreements: 0, order: 0 },
      );

      return (
        (medianOrder === 0
          ? pairwiseDisagreement.order
          : pairwiseDisagreement.disagreements > pairwiseDisagreement.agreements
            ? -medianSortOrderDifference
            : medianSortOrderDifference) ||
        a.inclusion.inclusion.localeCompare(b.inclusion.inclusion, undefined, { sensitivity: 'base' })
      );
    })
    .map(({ inclusion }) => inclusion);
}

export function buildPackageInclusionsFromMaster(
  masterInclusions: Array<MasterInclusion>,
  packageOptions: Array<PackageOption>,
): Record<string, Array<PackageInclusionRecord>> {
  return packageOptions.reduce<Record<string, Array<PackageInclusionRecord>>>((packageInclusions, packageOption) => {
    const assignedInclusions = masterInclusions
      .filter((inclusion) => inclusion.inclusion.trim() && inclusion.assignedPackageIds.includes(packageOption.id))
      .map((inclusion) => ({
        isUnique: inclusion.assignedPackageIds.length === 1,
        packageInclusion: {
          ...(inclusion.persistId ? { inclusion_id: inclusion.persistId } : { id: inclusion.id }),
          inclusion: {
            ...(inclusion.persistId ? { id: inclusion.persistId } : {}),
            name: inclusion.inclusion,
            category_id: inclusion.categoryId,
            currency: inclusion.currency,
            is_hidden: !!inclusion.isHidden,
            requires_booking: !!inclusion.requiresBooking,
            rrp: inclusion.rrp,
            type: inclusion.type,
            category: {
              name: inclusion.category,
            },
            unit: inclusion.unit,
          },
          lux_plus_tier: inclusion.luxPlusOnly ? 'base' : null,
          style: inclusion.highlighted ? 'highlighted' : null,
        },
      }));

    packageInclusions[packageOption.id] = assignedInclusions
      .filter((inclusion) => inclusion.isUnique)
      .concat(assignedInclusions.filter((inclusion) => !inclusion.isUnique))
      .map(({ packageInclusion }) => packageInclusion);

    return packageInclusions;
  }, {});
}

export function getChangedMasterInclusions(
  inclusions: Array<MasterInclusion>,
  changedInclusionIds: ReadonlySet<string>,
): Array<MasterInclusion> {
  return inclusions.filter((inclusion) => changedInclusionIds.has(inclusion.id));
}

export function preserveMasterInclusionOrder(
  currentInclusions: Array<MasterInclusion>,
  nextInclusions: Array<MasterInclusion>,
) {
  const nextInclusionsById = new Map(nextInclusions.map((inclusion) => [inclusion.id, inclusion]));

  return currentInclusions
    .flatMap((inclusion) => {
      const nextInclusion = nextInclusionsById.get(inclusion.id);

      if (!nextInclusion) {
        return [];
      }

      nextInclusionsById.delete(inclusion.id);

      return [nextInclusion];
    })
    .concat(Array.from(nextInclusionsById.values()));
}

export function getPackageInclusionIdsToUnassign(
  masterInclusions: Array<MasterInclusion>,
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<string> {
  if (!packageInclusions) {
    return [];
  }

  const assignedPackageIdsByInclusionId = new Map(
    masterInclusions.flatMap((inclusion) =>
      inclusion.persistId ? [[inclusion.persistId, new Set(inclusion.assignedPackageIds)] as const] : [],
    ),
  );

  return Object.entries(packageInclusions).flatMap(([packageId, inclusions]) =>
    inclusions.flatMap((rawInclusion) => {
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionId = getInclusionPersistId(inclusion);
      const rowId = rawInclusion.id ? String(rawInclusion.id) : undefined;
      const assignedPackageIds = inclusionId ? assignedPackageIdsByInclusionId.get(inclusionId) : undefined;

      if (!inclusionId || !rowId || !assignedPackageIds || isInclusionHidden(inclusion)) {
        return [];
      }

      return assignedPackageIds.has(packageId) ? [] : [rowId];
    }),
  );
}

export function getPackageInclusionIdsForMasterInclusion(
  masterInclusion: MasterInclusion,
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<string> {
  if (!masterInclusion.persistId || !packageInclusions) {
    return [];
  }

  return Object.values(packageInclusions).flatMap((inclusions) =>
    inclusions.flatMap((rawInclusion) => {
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const rowId = rawInclusion.id ? String(rawInclusion.id) : undefined;

      return rowId && getInclusionPersistId(inclusion) === masterInclusion.persistId && !isInclusionHidden(inclusion)
        ? [rowId]
        : [];
    }),
  );
}
